import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardProvider, useDashboard } from './DashboardContext';
import PeriodPicker from '../widgets/PeriodPicker';
import TitleCardWidget from '../widgets/TitleCardWidget';
import TableCard from '../widgets/TableCard';
import StatCard from '../widgets/StatCard';
import StatusPicker from '../widgets/StatusPicker';
import TenantDetailModal from '../widgets/TenantDetailModal';
import { formatDateTimeISO } from '../../utils/date';
import styles from './Dashboard.module.css';

import {
    useSystemDashboardTenantCount as useTenantCount,
    useSystemDashboardPersonalCount as usePersonalCount,
    useSystemDashboardTenantList as useTenantList,
    useSystemDashboardTenantDetail as useTenantDetail,
    useSystemDashboardTenantUsage as useTenantUsage,
    useSystemDashboardServiceUsage as useServiceUsage,
    useSystemDashboardServiceHistory as useServiceHistory,
} from '../../hooks';

// 요금제 정보 매핑 (TenantDetailModal 내부에서 사용)
const PLAN_MAP = {
    1: { name: 'Basic Plan (종량제)', discount: 0 },
    2: { name: 'Personal Plan (정액제)', discount: 0 },
    3: { name: 'Team Plan', discount: 5 },
    4: { name: 'Business Plan', discount: 15 },
    5: { name: 'Enterprise Plan', discount: 30 },
    6: { name: 'Enterprise Plus', discount: null }, // 별도 입력
};

const SERVICE_REQUEST_TENANT_MAX_ROWS = 4;

const SERVICE_HISTORY_MAX_ROWS = 10;
const SERVICE_HISTORY_COLUMNS = [
    { key: 'date', header: '날짜', width: '240px', align: 'center' },
    { key: 'model', header: '서비스명', width: '1fr', align: 'center' },
    { key: 'id', header: '시뮬레이션ID', width: '320px', align: 'center' },
    { key: 'floor', header: '층', width: '70px', align: 'center' },
    { key: 'pos', header: '발화 구역(x, y)', width: '260px', align: 'center' },
    { key: 'hrr', header: '발열량(HRR)', width: '110px', align: 'center' },
    { key: 'thresholds', header: '임계값(CO/온도/가시거리)', width: '200px', align: 'center' },
    { key: 'user', header: '사용자 이름', width: '140px', align: 'center' },
];
const SERVICE_HISTORY_SEARCH_FIELDS = [
    { value: 'all', label: '전체' },
    { value: 'date', label: '날짜' },
    { value: 'model', label: '서비스명' },
    { value: 'id', label: '시뮬레이션ID' },
    { value: 'user', label: '사용자 이름' },
];
// content.z 인덱스 → 층 표시값 (FloorPicker의 ziFromFloor 역변환)
const floorFromZi = (z) => (z === 0 ? 'B2' : z === 1 ? 'B1' : `${z - 1}F`);

const HISTORY_PER_TENANT_MAX_ROWS = 5;
const HISTORY_PER_TENANT = [
    { key: 'name', header: '테넌트 이름', width: '1.5fr', align: 'center', search: true },
    { key: 'traffic', header: '트래픽 사용량', width: '250px', align: 'center' },
    { key: 'storage', header: '스토리지 사용량', width: '250px', align: 'center' },
    { key: 'count', header: '수행 횟수', width: '150px', align: 'center' },
    { key: 'cost', header: '이용 요금', width: '200px', align: 'center' },
    { key: 'type', header: '타입', width: '100px', align: 'center' },
];
const HISTORY_PER_SERVICE_MAX_ROWS = 5;
const HISTORY_PER_SERVICE = [
    { key: 'name', header: '서비스 이름', width: '1fr', align: 'center', search: true },
    { key: 't_count', header: '테넌트 수', width: '150px', align: 'center' },
    { key: 'p_count', header: '사용자 수', width: '150px', align: 'center' },
    { key: 'traffic', header: '트래픽 사용량', width: '250px', align: 'center' },
    { key: 'storage', header: '스토리지 사용량', width: '250px', align: 'center' },
];

function formatBytes(bytes) {
    if (bytes === null || isNaN(bytes)) return { value: 0, unit: 'Byte' };
    if (bytes === 0) return { value: 0, unit: 'Byte' };

    const units = ['Byte', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);

    return { value: +value.toFixed(2), unit: units[i] };
}

function parseContent(content) {
    try {
        return typeof content === 'string' ? JSON.parse(content) : content ?? {};
    } catch {
        return {};
    }
}

function ServiceHistoryPanel({ onUserClick }) {
    const [field, setField] = useState('all');
    const [keyword, setKeyword] = useState('');
    const [search, setSearch] = useState({ field: 'all', keyword: '' });
    const [page, setPage] = useState(1);
    const { data: historyRes, isFetching } = useServiceHistory(search.field, search.keyword, page, SERVICE_HISTORY_MAX_ROWS);

    const handleSearch = () => {
        setSearch({ field, keyword });
        setPage(1);
    };
    useEffect(() => {
        if (keyword.length === 0) {
            setSearch((s) => ({ ...s, keyword: '' }));
            setPage(1);
        }
    }, [keyword]);

    const rows = useMemo(
        () =>
            (historyRes?.items ?? []).map((it) => {
                const c = parseContent(it.content);
                const th = c.thresholds ?? {};
                const num = (v) => (typeof v === 'number' ? +v.toFixed(2) : v ?? '-');
                return {
                    date: formatDateTimeISO(it.working_date, { dateStyle: 'medium', timeStyle: 'medium' }) ?? '',
                    model: it.model_name ?? '',
                    id: it.working_id ?? '',
                    floor: typeof c.z === 'number' ? floorFromZi(c.z) : '-',
                    pos: c.x != null && c.y != null ? `${it.section_name ? `${it.section_name} ` : ''}(${num(c.x)}, ${num(c.y)})` : it.section_name ?? '-',
                    hrr: c.hrr_density ?? '-',
                    thresholds: `${th.co ?? '-'} / ${th.temp ?? '-'} / ${th.vis ?? '-'}`,
                    user: it.user_name ?? '',
                    tenant_key: it.tenant_key,
                };
            }),
        [historyRes]
    );

    // 사용자 이름 클릭 → 테넌트 상세 팝업
    const columns = useMemo(
        () =>
            SERVICE_HISTORY_COLUMNS.map((c) =>
                c.key === 'user'
                    ? {
                          ...c,
                          accessor: (row) =>
                              row.tenant_key != null ? (
                                  <span
                                      role="button"
                                      tabIndex={0}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => onUserClick?.(row.tenant_key)}
                                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onUserClick?.(row.tenant_key)}
                                  >
                                      {row.user}
                                  </span>
                              ) : (
                                  row.user
                              ),
                      }
                    : c
            ),
        [onUserClick]
    );

    return (
        <section className={styles.subsection}>
            <TitleCardWidget
                title="서비스 이용 내역"
                titleClassName="cardBgLightBlue cardTitleWhite"
                headerRight={
                    <div className={styles.tableActions} style={{ display: 'flex', gap: 8, justifySelf: 'end' }}>
                        <div className={styles.searchBox}>
                            <select
                                value={field}
                                onChange={(e) => setField(e.target.value)}
                                aria-label="검색 대상"
                                style={{ height: 36, border: 'none', borderRight: '1px solid #cfd7e3', padding: '0 8px', outline: 'none', background: '#fff' }}
                            >
                                {SERVICE_HISTORY_SEARCH_FIELDS.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="search"
                                placeholder={field === 'date' ? '예: 2026-10-22' : '검색어 입력'}
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                aria-label="검색어 입력"
                            />
                            <button className={styles.iconBtn} onClick={handleSearch} aria-label="검색">
                                🔍
                            </button>
                        </div>
                    </div>
                }
            >
                <TableCard
                    data={rows}
                    columns={columns}
                    rows={SERVICE_HISTORY_MAX_ROWS}
                    pagenateSize={SERVICE_HISTORY_MAX_ROWS}
                    page={page}
                    total={historyRes?.total ?? 0}
                    onPageChange={setPage}
                    loading={isFetching}
                />
            </TitleCardWidget>
        </section>
    );
}

function TenantUsageInner() {
    const { year, month } = useDashboard();
    const [tenantName, setTenantName] = useState('');
    const [searchTenantName, setSearchTenantName] = useState('');
    const [tenantPage, setTenantPage] = useState(1);
    const { data: tenantUsage, isFetching: isTenantLoading } = useTenantUsage(searchTenantName, year, month, tenantPage, HISTORY_PER_TENANT_MAX_ROWS);

    useEffect(() => {
        if (tenantName?.length === 0) setTenantPage(1);
    }, [tenantName]);
    useMemo(() => {
        if (tenantName?.length === 0) setSearchTenantName(tenantName);
    }, [tenantName]);
    const handleSearchTenantName = () => setSearchTenantName(tenantName);

    const serverTenantHistory = useMemo(
        () =>
            (tenantUsage?.items ?? []).map((r) => {
                const { value: tVal, unit: tUnit } = formatBytes(r.traffic);
                const { value: sVal, unit: sUnit } = formatBytes(r.storage);

                return {
                    name: String(r.name),
                    traffic: `${tVal} ${tUnit}`,
                    storage: `${sVal} ${sUnit}`,
                    count: r.count,
                    cost: `${Number(r.cost ?? 0).toLocaleString()}원`,
                    type: r.type === 1 ? '개인' : '조직',
                };
            }),
        [tenantUsage]
    );

    return (
        <section className={styles.subsection}>
            <TitleCardWidget
                title="테넌트별 이용 내역"
                titleClassName="cardBgLightBlue cardTitleWhite"
                headerRight={
                    <div className={styles.tableActions} style={{ display: 'flex', gap: 8, justifySelf: 'end' }}>
                        <div className={styles.searchBox}>
                            <input
                                type="search"
                                placeholder="테넌트 검색"
                                value={tenantName}
                                onChange={(e) => setTenantName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchTenantName()}
                                aria-label="검색어 입력"
                            />
                            <button className={styles.iconBtn} onClick={handleSearchTenantName} aria-label="검색">
                                🔍
                            </button>
                        </div>
                        <PeriodPicker />
                    </div>
                }
            >
                <TableCard
                    data={serverTenantHistory}
                    columns={HISTORY_PER_TENANT}
                    rows={HISTORY_PER_TENANT_MAX_ROWS}
                    total={tenantUsage?.total}
                    page={tenantPage}
                    pagenateSize={HISTORY_PER_TENANT_MAX_ROWS}
                    onPageChange={setTenantPage}
                    loading={isTenantLoading}
                />
            </TitleCardWidget>
        </section>
    );
}

function ServiceUsageInner() {
    const { year, month } = useDashboard();
    const [serviceName, setServiceName] = useState('');
    const [searchServiceName, setSearchServiceName] = useState('');
    const [servicePage, setServicePage] = useState(1);
    const { data: serviceUsage, isFetching: isServiceLoading } = useServiceUsage(searchServiceName, year, month, servicePage, HISTORY_PER_TENANT_MAX_ROWS);

    useEffect(() => {
        if (serviceName?.length === 0) setServicePage(1);
    }, [serviceName]);
    useMemo(() => {
        if (serviceName?.length === 0) setSearchServiceName(serviceName);
    }, [serviceName]);
    const handleSearchServiceName = () => setSearchServiceName(serviceName);

    const serverServiceHistory = useMemo(
        () =>
            (serviceUsage ?? []).map((r) => {
                const { value: tVal, unit: tUnit } = formatBytes(r.traffic);
                const { value: sVal, unit: sUnit } = formatBytes(r.storage);

                return {
                    name: String(r.name),
                    t_count: r.t_count,
                    p_count: r.p_count,
                    traffic: `${tVal} ${tUnit}`,
                    storage: `${sVal} ${sUnit}`,
                };
            }),
        [serviceUsage]
    );

    return (
        <section className={styles.subsection}>
            <TitleCardWidget
                title="서비스별 이용 내역"
                titleClassName="cardBgLightBlue cardTitleWhite"
                headerRight={
                    <div className={styles.tableActions} style={{ display: 'flex', gap: 8, justifySelf: 'end' }}>
                        <div className={styles.searchBox}>
                            <input
                                type="search"
                                placeholder="서비스 검색"
                                value={serviceName}
                                onChange={(e) => setServiceName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchServiceName()}
                                aria-label="검색어 입력"
                            />
                            <button className={styles.iconBtn} onClick={handleSearchServiceName} aria-label="검색">
                                🔍
                            </button>
                        </div>
                        <PeriodPicker />
                    </div>
                }
            >
                <TableCard
                    data={serverServiceHistory}
                    columns={HISTORY_PER_SERVICE}
                    rows={HISTORY_PER_SERVICE_MAX_ROWS}
                    page={servicePage}
                    total={serverServiceHistory.length}
                    pagenateSize={HISTORY_PER_SERVICE_MAX_ROWS}
                    loading={isServiceLoading}
                    onPageChange={setServicePage}
                />
            </TitleCardWidget>
        </section>
    );
}

function TenantUsagePanel() {
    return (
        <DashboardProvider>
            <TenantUsageInner />
        </DashboardProvider>
    );
}

function ServiceUsagePanel() {
    return (
        <DashboardProvider>
            <ServiceUsageInner />
        </DashboardProvider>
    );
}

function AdminMenuPanel() {
    const navigate = useNavigate();

    return (
        <section className={styles.subsection}>
            <TitleCardWidget title="관리자 메뉴" titleClassName="cardBgLightBlue cardTitleWhite">
                <div className={styles.adminMenuGrid}>
                    <button type="button" className={styles.adminMenuCard} onClick={() => navigate('/admin/support/notices')}>
                        <h3>공지사항 관리</h3>
                        <p>공지 등록·수정·삭제 및 목록 관리</p>
                    </button>

                    <button type="button" className={styles.adminMenuCard} onClick={() => navigate('/admin/support/faqs')}>
                        <h3>FAQ 관리</h3>
                        <p>자주 묻는 질문 등록·수정·삭제</p>
                    </button>

                    <button type="button" className={styles.adminMenuCard} onClick={() => navigate('/admin/support/inquiries')}>
                        <h3>1:1 문의 관리</h3>
                        <p>사용자 문의 확인 및 답변, 상태 관리</p>
                    </button>
                </div>
            </TitleCardWidget>
        </section>
    );
}

function DashboardInner() {
    const { data: tenant } = useTenantCount();
    const { data: personal } = usePersonalCount();

    const [tenantName, setTenantName] = useState('');
    const [searchTenantName, setSearchTenantName] = useState('');
    const [tenantPage, setTenantPage] = useState(1);
    const { data: tenantListRes, isFetching } = useTenantList(searchTenantName, tenantPage, SERVICE_REQUEST_TENANT_MAX_ROWS);

    useEffect(() => {
        if (tenantName?.length === 0) setTenantPage(1);
    }, [tenantName]);
    useMemo(() => {
        if (tenantName?.length === 0) setSearchTenantName(tenantName);
    }, [tenantName]);
    const handleSearchTenantName = () => setSearchTenantName(tenantName);

    const [detailId, setDetailId] = useState(null);
    const openDetail = (id) => setDetailId(id);
    const closeDetail = () => setDetailId(null);
    const { data: detail } = useTenantDetail(detailId, !!detailId);
    const REQ_COLS = [
        {
            key: 'org_name',
            header: '테넌트 이름',
            width: '1fr',
            // ✅ 이제 클릭은 행에서 처리하므로, 텍스트만 표시
            accessor: (row) => row.org_name,
        },
        {
            key: 'created_at',
            header: '가입 날짜',
            width: '250px',
            align: 'center',
            accessor: (row) => formatDateTimeISO(row.created_at, { timeStyle: 'short' }),
        },
        {
            key: 'status',
            header: '상태',
            width: '120px',
            align: 'center',
            accessor: (row) => (
                <div
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()} // 드래그/포커스 시에도 행 클릭 방지
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                        }
                    }}
                >
                    <StatusPicker id={row.key} status={row.status} />
                </div>
            ),
        },
    ];

    return (
        <div className={styles.dash}>
            {/* 1행: 통계 + 테넌트 목록 */}
            <section className={styles.subsection}>
                <div className={styles.dashGrid112}>
                    <StatCard title="테넌트 현황" value={tenant?.count ?? 0} unit="" className={styles.cardBill} />
                    <StatCard title="사용자 현황" value={personal?.count ?? 0} unit="" className={styles.cardBill} />
                    <TitleCardWidget
                        title="테넌트 정보"
                        titleClassName="cardBgLightBlue cardTitleWhite"
                        headerRight={
                            <div className={styles.searchBox}>
                                <input
                                    type="search"
                                    placeholder="테넌트 검색"
                                    value={tenantName}
                                    onChange={(e) => setTenantName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchTenantName()}
                                    aria-label="검색어 입력"
                                />
                                <button className={styles.iconBtn} onClick={handleSearchTenantName} aria-label="검색">
                                    🔍
                                </button>
                            </div>
                        }
                    >
                        <TableCard
                            data={tenantListRes?.items ?? []}
                            columns={REQ_COLS}
                            rows={SERVICE_REQUEST_TENANT_MAX_ROWS}
                            pagenateSize={SERVICE_REQUEST_TENANT_MAX_ROWS}
                            page={tenantPage}
                            total={tenantListRes?.total ?? 0}
                            onPageChange={setTenantPage}
                            loading={isFetching}
                            // ✅ 행 클릭 시 모달 오픈
                            onRowClick={(row) => openDetail(row.key)}
                        />
                    </TitleCardWidget>
                </div>
            </section>

            {/* 2행 3행: 그대로 */}
            <ServiceHistoryPanel onUserClick={openDetail} />
            <TenantUsagePanel />
            <ServiceUsagePanel />

            {/* ✅ 상세 모달 */}
            <TenantDetailModal open={!!detailId} detail={detail} onClose={closeDetail} />
        </div>
    );
}

export default function DashboardSystem() {
    return (
        <DashboardProvider>
            <DashboardInner />
        </DashboardProvider>
    );
}
